import { defaultEnvironment } from '../environment';
import { HttpService } from './http';
import { UserService } from './user';
function parseUtcDate(value) {
  const hasTimezoneDesignator = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasTimezoneDesignator ? value : `${value}Z`);
}
export class GameService {
  static async getGame(id, {
    query
  }) {
    try {
      const res = await HttpService.get('v1', `/games/${id}`, [{
        key: 'query',
        value: query
      }]);
      return parseGame(res);
    } catch (err) {
      console.error('[GameService][getGame]', err);
      throw err;
    }
  }
  static async createPlayer(game) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.post('v2', `/games/${game.id}/players`, [{
        key: 'game_id',
        value: game.id
      }, {
        key: 'user_id',
        value: UserService.currentUser.id
      }]);
      return parseGamePlayer(game.theme, data);
    } catch (err) {
      console.error('[GameService][createPlayer]', err);
      throw err;
    }
  }
  static async getPlayer(game, {
    query
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const {
        results: [res]
      } = await HttpService.get('v2', `/user_games`, [{
        key: 'game_ids',
        value: game.id
      }, {
        key: 'page',
        value: 1
      }, {
        key: 'page_size',
        value: 1
      }, {
        key: 'query',
        value: query
      }]);
      return res ? parseGamePlayer(game.theme, res) : null;
    } catch (err) {
      console.error('[GameService][getPlayer]', err);
      throw err;
    }
  }
  static async getTeams(gameId, {
    groupIds,
    query
  } = {}) {
    try {
      const {
        results
      } = await HttpService.get('v1', `/games/${gameId}/teams`, [{
        key: 'ordering',
        value: 'leaderboard_index'
      }, {
        key: 'page',
        value: 1
      }, {
        key: 'page_size',
        value: 100
      }, ...(query ? [{
        key: 'query',
        value: query
      }] : []), ...(groupIds ? groupIds.map(id => ({
        key: 'group_ids',
        value: id
      })) : [])]);
      return results.reduce((res, data) => {
        const team = parseTeam(data);
        if (team) res.push(team);
        return res;
      }, []);
    } catch (err) {
      console.error('[GameService][getTeams]', err);
      throw err;
    }
  }
  static async getTiles(gameId, {
    bounds,
    onlyConnectedUser
  } = {}) {
    try {
      const queryParams = [];
      if (bounds) {
        const {
          north,
          east,
          south,
          west
        } = bounds;
        queryParams.push({
          key: 'north',
          value: north
        }, {
          key: 'east',
          value: east
        }, {
          key: 'south',
          value: south
        }, {
          key: 'west',
          value: west
        });
      }
      if (onlyConnectedUser) {
        queryParams.push({
          key: 'only_connected_user',
          value: true
        });
      }
      const res = await HttpService.get('v1', `/games/${gameId}/tile_states`, queryParams);
      return res.reduce((res, data) => {
        const tileState = parseTile(data);
        if (tileState) res.push(tileState);
        return res;
      }, []);
    } catch (err) {
      console.error('[GameService][getTiles]', err);
      throw err;
    }
  }
  static async getTileState(gameId, tileStateId) {
    try {
      const res = await HttpService.get('v1', `/games/${gameId}/tile_states/${tileStateId}`);
      return parseTile(res);
    } catch (err) {
      console.error('[GameService][getTileState]', err);
      throw err;
    }
  }
  static async getLeaderboard(game, {
    page,
    pageSize,
    query
  }) {
    try {
      const {
        count,
        results
      } = await HttpService.get('v1', `/games/${game.id}/players`, [{
        key: 'ordering',
        value: 'leaderboard_index'
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'query',
        value: query
      }]);
      return {
        count,
        leaderboard: results.reduce((res, data) => {
          const player = parseGamePlayer(game.theme, data);
          if (player) res.push(player);
          return res;
        }, [])
      };
    } catch (err) {
      console.error('[GameService][getLeaderboard]', err);
      throw err;
    }
  }
  static async getCompaniesLeaderboard(game, {
    page,
    pageSize,
    query
  }) {
    try {
      const {
        count,
        results
      } = await HttpService.get('v1', `/games/${game.id}/company_rank`, [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'query',
        value: query
      }]);
      return {
        count,
        leaderboard: results.reduce((res, data) => {
          const team = parseTeam(data);
          if (team) res.push(team);
          return res;
        }, [])
      };
    } catch (err) {
      console.error('[GameService][getCompaniesLeaderboard]', err);
      throw err;
    }
  }
  static async getCyclabilityZonesLeaderboard(game, {
    page,
    pageSize
  }) {
    try {
      const response = await HttpService.get('v1', `/games/${game.id}/cyclability_zone_rank`, [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }]);
      const rawItems = Array.isArray(response) ? response : Array.isArray(response.results) ? response.results : Array.isArray(response.leaderboard) ? response.leaderboard : [];
      return {
        count: typeof response.count === 'number' ? response.count : rawItems.length,
        leaderboard: rawItems.reduce((res, data) => {
          const item = parseCyclabilityZoneLeaderboardItem(data);
          if (item && item.score > 0) res.push(item);
          return res;
        }, [])
      };
    } catch (err) {
      console.error('[GameService][getCyclabilityZonesLeaderboard]', err);
      throw err;
    }
  }
  static async getCyclabilityZoneScore(gameId, cyclabilityZoneId) {
    try {
      const response = await HttpService.get('v1', `/games/${gameId}/cyclability_zone_score/${cyclabilityZoneId}`);
      return parseCyclabilityZoneScore(response);
    } catch (err) {
      console.error('[GameService][getCyclabilityZoneScore]', err);
      throw err;
    }
  }
}
const gameThemesMap = {
  CHRISTMAS_V1: 'christmasV1',
  HALLOWEEN_V1: 'halloweenV1',
  HALLOWEEN_2026: 'halloween2026',
  PEDALBLOOM_V1: 'pedalbloomV1',
  BIKE_FOR_GOOD_2026: 'bikeForGood2026'
};
function parseGame({
  bloom_thresholds,
  theme: _theme,
  id,
  title,
  mode,
  description,
  photo,
  start_datetime,
  settings,
  end_datetime,
  team_assignation_mode,
  covered_zone,
  last_compute_end_datetime,
  last_compute_start_datetime,
  last_pmtiles_generation_end_datetime,
  last_processed_trace_creation_time,
  pmtiles_file
}) {
  const normalizedTheme = _theme?.toUpperCase();
  const theme = normalizedTheme && gameThemesMap[normalizedTheme] || (mode === 'BLOOM' ? 'pedalbloomV1' : null);
  if (!theme) throw new Error('empty or unknown theme');
  const rawBloomThresholds = bloom_thresholds ?? settings?.bloom_thresholds;
  const parsedBloomThresholds = Array.isArray(rawBloomThresholds) && rawBloomThresholds.length ? rawBloomThresholds.map(value => Number(value)).filter(value => Number.isFinite(value) && value > 0).sort((a, b) => a - b) : null;
  const startDate = start_datetime ? new Date(start_datetime) : null;
  const endDate = end_datetime ? new Date(end_datetime) : null;
  if (startDate) startDate.setHours(0, 0, 0);
  if (endDate) endDate.setHours(23, 59, 59);
  return {
    bloomThresholds: parsedBloomThresholds,
    id: id || -1,
    title: title || null,
    settings: settings || null,
    theme,
    mode: mode || null,
    description: description || null,
    photo: photo ? `${defaultEnvironment.backendUrl}${photo}` : null,
    startDate,
    endDate,
    teamAssignationMode: team_assignation_mode === 'MANUAL_USER_CHOOSE_TEAM' ? 'manual' : 'automatic',
    coveredZone: covered_zone || null,
    lastComputeEndDate: last_compute_end_datetime ? parseUtcDate(last_compute_end_datetime) : null,
    lastComputeStartDate: last_compute_start_datetime ? parseUtcDate(last_compute_start_datetime) : null,
    lastPmtilesGenerationEndDate: last_pmtiles_generation_end_datetime ? parseUtcDate(last_pmtiles_generation_end_datetime) : null,
    lastProcessedTraceCreationTime: last_processed_trace_creation_time ? parseUtcDate(last_processed_trace_creation_time) : null,
    pmTilesFileUrl: pmtiles_file ? `${defaultEnvironment.backendUrl}${pmtiles_file.trim()}` : null
  };
}
function parseTeam({
  id,
  title,
  color,
  photo,
  icon,
  rank,
  leaderboard_index,
  score,
  challenge,
  metadata
}) {
  if (!id) return null;
  return {
    id,
    title: title || null,
    color: color || null,
    groupId: challenge?.group || null,
    photo: photo ? `${defaultEnvironment.backendUrl}${photo}` : null,
    icon: icon ? `${defaultEnvironment.backendUrl}${icon}` : null,
    rank: rank || null,
    leaderboardIndex: leaderboard_index || null,
    score: score || 0,
    visitedTileCount: metadata?.visited_tile_count || 0,
    commutingVisitedTileCount: metadata?.commuting_visited_tile_count || 0,
    visitCountQuantiles: metadata?.visit_count_quantiles || [],
    commutingVisitCountQuantiles: metadata?.commuting_visit_count_quantiles || []
  };
}
function parseCyclabilityZoneLeaderboardItem({
  centroid,
  id,
  leaderboard_index,
  name,
  rank,
  score
}) {
  if (!id || !name) return null;
  const parsedCentroid = centroid?.type === 'Point' && Array.isArray(centroid.coordinates) && centroid.coordinates.length >= 2 && centroid.coordinates.every(coordinate => Number.isFinite(coordinate)) ? centroid : null;
  return {
    centroid: parsedCentroid,
    id,
    leaderboardIndex: leaderboard_index || null,
    name,
    rank: rank || null,
    score: typeof score === 'number' && Number.isFinite(score) ? score : 0
  };
}
function parseCyclabilityZoneScore({
  id,
  name,
  score,
  total_tiles,
  total_visited_tiles
}) {
  return {
    id: typeof id === 'number' && Number.isFinite(id) ? id : -1,
    name: name || null,
    score: typeof score === 'number' && Number.isFinite(score) ? score : 0,
    totalTiles: typeof total_tiles === 'number' && Number.isFinite(total_tiles) ? total_tiles : 0,
    totalVisitedTiles: typeof total_visited_tiles === 'number' && Number.isFinite(total_visited_tiles) ? total_visited_tiles : 0
  };
}
function parseTile({
  h3_index,
  id,
  team,
  metadata
}) {
  if (!h3_index) return null;
  const {
    last_event,
    last_event_datetime
  } = metadata || {};
  return {
    h3Index: h3_index,
    id: id ?? null,
    lastEvent: isGameTileLastEvent(last_event) ? last_event : null,
    lastEventDatetime: last_event_datetime ? parseUtcDate(last_event_datetime) : null,
    teamId: team || null,
    metadata: metadata || null
  };
}
function isGameTileLastEvent(value) {
  return value === 'TILE_CAPTURED' || value === 'SHIELD_GAINED' || value === 'SHIELD_LOST' || value === 'TILE_TIED' || value === 'SHIELD_FULL';
}
function parseGamePlayer(theme, {
  id,
  team,
  teams,
  rank,
  leaderboard_index,
  score,
  user,
  metadata
}) {
  if (!id) return null;
  const teamId = team?.id ?? teams?.[0]?.id ?? -1;
  return {
    id,
    teamId,
    rank: rank || null,
    leaderboardIndex: leaderboard_index || null,
    score: score || 0,
    username: user?.username || `Player ${id}`,
    profilePicture: user?.profile_picture ? `${defaultEnvironment.backendUrl}${user.profile_picture}` : null,
    metadata: metadata && theme === 'christmasV1' ? {
      christmasV1: {
        gifts: new Array(24).fill(null).map((_, index) => {
          const count = metadata[`present_${index + 1}`];
          if (typeof count === 'number') return count;
          return 0;
        })
      }
    } : {}
  };
}